import React, {Component} from "react"
import {chunk} from "lodash"
import {eachSeries} from "async"

import {User} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {Label, H4, Body} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import workosService from "@irati/workos/services/workos"
import theme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import userService from "@irati/identity/services/users"
import Loading from "@irati/ui/src/Loading"
import ProfilePicture from "@irati/ui/src/ProfilePicture"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {forgotPassword} from "@irati/auth/services/password"
import Search from "@irati/ui/src/Search"
import {adminDashboardTracking} from "@irati-tracking/admin"

import GenericModal from "@irati/ui/src/GenericModal"

import okta from "@irati/ui/images/okta.svg"
import workDay from "@irati/ui/images/workday.svg"
import google from "@irati/ui/images/google.svg"

import {Image, ManyMore, UsersWrapper} from "../../Dashboard/Integrations/styled"
import {SETTINGS_LOGIN_INTEGRATION} from "../../../routes/routeConfig"

const MAXIMUM_USERS_HEIGHT_IN_MODAL = "220px"

interface Props extends InjectIntl {}

interface State {
  load: Promise<void> | null,
  submit: Promise<void> | null,
  showModal: boolean,
  showSuccess: boolean,
  showIntegrationsPrompt: boolean,
  isOrganisationIntegrated: boolean,
  users: User[],
  usersToSendInvitesTo: User[],
  search?: string
}

class SendInvitesModal extends Component<Props, State> {
  getDefaultState = () => {
    return {
      showModal: false,
      load: null,
      submit: null,
      showSuccess: false,
      showIntegrationsPrompt: false,
      isOrganisationIntegrated: false,
      users: [],
      usersToSendInvitesTo: [],
      search: ""
    }
  }

  state: State = {
    ...this.getDefaultState()
  }

  componentDidMount() {
    const load = this.getUsers()
    this.setState({load})
  }

  getUsers = (usersFromPreviousRequest?: User[] = [], cursor?: String) => {
    return userService.getNonOnboardedUsersForBusiness({cursor}).then((res) => {
      const users = [...usersFromPreviousRequest, ...res.results]
      if (res.cursor) {
        return this.getUsers(users, res.cursor)
      }
      this.setState({users, usersToSendInvitesTo: users})
    })
  }

  show = () => {
    this.setState({showModal: true})
  }

  handleSendInvites = () => {
    const {usersToSendInvitesTo} = this.state
    const submit = new Promise(() => {
      const chunks = chunk(usersToSendInvitesTo, 10)
      eachSeries(chunks, (users, done) => {
        const requests = users.map(({email}) => forgotPassword(email, authentication.business()))
        return Promise.all(requests).then(() => done())
      }, () => {
        return workosService.getOrganisation(authentication.business().uuid).then((organisation) => {
          this.setState({showSuccess: true, isOrganisationIntegrated: organisation?.connectionId && organisation?.directoryId})
          adminDashboardTracking.clickSendInvites()
        })
      })
    })
    this.setState({submit})
  }

  getTitle = () => {
    const {showSuccess, showIntegrationsPrompt} = this.state
    if (showSuccess) {
      return "admin.components.sendInvitesModal.invitationSent.title"
    }
    if (showIntegrationsPrompt) {
      return "admin.components.sendInvitesModal.integrate.title"
    }
    return "admin.components.sendInvitesModal.title"
  }

  getBody = () => {
    const {showSuccess, showIntegrationsPrompt} = this.state
    if (showSuccess) {
      return "admin.components.sendInvitesModal.invitationSent.body"
    }
    if (showIntegrationsPrompt) {
      return "admin.components.sendInvitesModal.integrate.body"
    }
    return "admin.components.sendInvitesModal.body"
  }

  handleAddOrRemoveUser = (user: User) => {
    this.setState((prevState: State) => {
      const index = prevState.usersToSendInvitesTo.findIndex(({uuid}) => uuid === user.uuid)
      if (index > -1) {
        const updatedArray = [...prevState.usersToSendInvitesTo]
        updatedArray.splice(index, 1)
        adminDashboardTracking.clickRemoveInvites()
        return ({usersToSendInvitesTo: updatedArray})
      }
      adminDashboardTracking.clickAddInvites()
      return ({usersToSendInvitesTo: [...prevState.usersToSendInvitesTo, user]})
    })
  }

  searchUsers = (user: User) => {
    const {search} = this.state
    if (!search) {
      return true
    }
    let doesUserContainSearchValue = false
    search.split(" ").forEach((valueToTest: string) => {
      if (valueToTest.length > 0) {
        const doesFirstNameContainSearchValue = user.firstName.toLowerCase().includes(valueToTest.toLowerCase())
        const doesLastNameContainSearchValue = user.lastName.toLowerCase().includes(valueToTest.toLowerCase())
        const doesEmailContainSearchValue = user.email.toLowerCase().includes(valueToTest.toLowerCase())
        doesUserContainSearchValue = doesFirstNameContainSearchValue || doesLastNameContainSearchValue || doesEmailContainSearchValue
      }
    })
    return doesUserContainSearchValue
  }

  handleSuccessClick = () => {
    const {isOrganisationIntegrated} = this.state
    if (!isOrganisationIntegrated) {
      this.setState({showSuccess: false, showIntegrationsPrompt: true})
    } else {
      this.handleClose()
    }
  }

  handleClose = () => {
    adminDashboardTracking.closeSendInviteModule()
    this.setState({...this.getDefaultState(), users: this.state.users, usersToSendInvitesTo: this.state.users})
  }

  handleIntegrationsPromptClose = () => {
    adminDashboardTracking.clickSendInvitesMaybeLater()
    this.setState({...this.getDefaultState()})
    this.setState({...this.getDefaultState(), users: this.state.users, usersToSendInvitesTo: this.state.users})
  }

  handleIntegrationsPrompt = () => {
    adminDashboardTracking.clickSendInvitesTellMeMore()
  }

  render() {
    const {formatMessage} = this.props
    const {submit, showModal, showSuccess, showIntegrationsPrompt, users, usersToSendInvitesTo, load, search} = this.state

    const title = this.getTitle()
    const body = this.getBody()

    return (
      <GenericModal
        show={showModal}
        hideActions
        title={formatMessage({id: title})}
        onConfirm={this.handleSendInvites}
        onHide={this.handleClose}>
        <Body marginBottom={3}>
          <FormattedMessage id={body} />
        </Body>
        {showIntegrationsPrompt &&
          <Flex margin={[4, 0]} gap={theme.spacing.oneAndHalf}>
            <Image src={okta} padding={1} alt="okta" />
            <Image src={workDay} padding={2} alt="workDay" />
            <Image src={google} padding={2} alt="google" />
            <ManyMore>
              <FormattedMessage id="admin.dashboard.integrations.manyMore" />
            </ManyMore>
          </Flex>
        }
        {showSuccess &&
          <Button primary onClick={this.handleSuccessClick}>
            <FormattedMessage id="admin.dashboard.integrations.feedbackSuccess.button" />
          </Button>
        }
        {showIntegrationsPrompt &&
          <>
            <ButtonRouterLink primary to={SETTINGS_LOGIN_INTEGRATION} onClick={this.handleIntegrationsPrompt}>
              <FormattedMessage id="admin.dashboard.integrations.dsyncPrompt.button" />
            </ButtonRouterLink>
            <Button marginLeft onClick={this.handleIntegrationsPromptClose}>
              <FormattedMessage id="admin.dashboard.integrations.dsyncPrompt.cancel" />
            </Button>
          </>

        }
        {!showSuccess && !showIntegrationsPrompt &&
          <Loading load={load}>
            <Flex direction="column" marginBottom={3}>
              <Search onChange={(searchTerm: string) => this.setState({search: searchTerm})} value={search} />
              <UsersWrapper maxHeight={MAXIMUM_USERS_HEIGHT_IN_MODAL} margin={[2, 0]}>
                {users.filter(this.searchUsers).map((user) =>
                  <Flex justifyContent="space-between" alignItems="center" marginBottom={2}>
                    <Flex alignItems="center">
                      <ProfilePicture user={user} regular />
                      <H4 marginLeft={2}>{user.firstName} {user.lastName}</H4>
                    </Flex>
                    <Button small onClick={() => this.handleAddOrRemoveUser(user)}>
                      {usersToSendInvitesTo.find(({uuid}) => uuid === user.uuid)
                        ? <FormattedMessage id="admin.dashboard.integrations.users.button.remove" />
                        : <FormattedMessage id="admin.dashboard.integrations.users.button.add" />
                      }
                    </Button>
                  </Flex>
                )}
                {search && users.filter(this.searchUsers).length === 0 &&
                  <Body marginBottom={3}>
                    <FormattedMessage id="admin.dashboard.integrations.users.emptySearch" />
                  </Body>
                }
              </UsersWrapper>
            </Flex>
            <Flex justifyContent="space-between">
              <Button primary onClick={this.handleSendInvites} load={submit}>
                <FormattedMessage id="admin.dashboard.integrations.button.sendInvites" />
              </Button>
              <Flex direction="column" alignItems="flex-end">
                <Label small>
                  <FormattedMessage id="admin.dashboard.integrations.label" />
                </Label>
                <H4>{usersToSendInvitesTo.length}</H4>
              </Flex>
            </Flex>
          </Loading>
        }

      </GenericModal>
    )
  }
}

export default injectIntl(SendInvitesModal, {withRef: true})
